import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import HandymanCard from "../components/HandymanCard";

const HERO_IMG = "https://images.pexels.com/photos/19916700/pexels-photo-19916700.jpeg";

export default function Landing() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/handymen").then((r) => setFeatured(r.data.slice(0, 3)));
  }, []);

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.label]));

  return (
    <div className="bg-cream">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-beige px-4 py-1.5 text-xs tracking-widest uppercase text-charcoal/70" data-testid="hero-eyebrow">
              <Sparkles className="w-3.5 h-3.5 text-terracotta" /> For students & parents
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-charcoal">
              Move-in help,<br />
              <span className="italic text-terracotta">handled.</span>
            </h1>
            <p className="mt-6 text-lg text-charcoal/75 max-w-xl leading-relaxed">
              Book trusted, campus-vetted pros for furniture assembly, TV mounts, moving help and cleaning — so your first night in the new place isn't spent wrestling an IKEA box.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => nav("/browse")} data-testid="cta-browse" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white px-7 h-12 btn-lift">
                Find a Move-in Pro <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
              <Button onClick={() => nav("/register?role=handyman")} data-testid="cta-join" variant="outline" className="rounded-full border-charcoal/20 text-charcoal hover:bg-beige px-7 h-12">
                Work as a Move-in Pro
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-charcoal/70">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-sage" /> Vetted pros</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-sage" /> Secure payments</span>
              <span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-sage" /> Same-day help</span>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="absolute -top-6 -left-6 w-40 h-40 bg-sage/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -right-8 w-52 h-52 bg-terracotta/20 rounded-full blur-3xl" />
            <div className="relative rounded-3xl overflow-hidden border border-[#1F2A40] shadow-2xl shadow-charcoal/10">
              <img src={HERO_IMG} alt="Warm, settled dorm room" className="w-full h-[520px] object-cover" />
              <div className="absolute bottom-5 left-5 right-5 bg-[#131C2E]/90 backdrop-blur-md rounded-2xl p-4 border border-[#1F2A40]">
                <div className="flex items-center gap-3">
                  <img src="https://images.unsplash.com/photo-1461938337379-4b537cd2db74" alt="" className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <p className="font-medium text-charcoal">Marcus just assembled a bed frame at NYU</p>
                    <p className="text-xs text-charcoal/60">2 minutes ago · ⭐ 4.9</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="how" className="py-20 bg-beige/50 grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-terracotta font-medium">What we do</span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-charcoal mt-2">Pick a service</h2>
            </div>
            <Link to="/browse" className="text-sm text-charcoal/70 hover:text-terracotta transition-colors" data-testid="view-all-services">
              View all Move-in Pros <ArrowRight className="inline w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((c, i) => (
              <Link key={c.id} to={`/browse?category=${c.id}`} data-testid={`category-${c.id}`}
                className="group relative rounded-2xl overflow-hidden border border-[#1F2A40] card-hover animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}>
                <div className="aspect-[5/3] overflow-hidden bg-[#131C2E]">
                  <img src={c.image} alt={c.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5 bg-[#131C2E]">
                  <h3 className="font-display text-xl text-charcoal">{c.label}</h3>
                  <p className="text-sm text-charcoal/60 mt-1">Browse available pros →</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-xs tracking-[0.2em] uppercase text-terracotta font-medium">Featured</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-charcoal mt-2">Meet a few favorites</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((h) => <HandymanCard key={h.user_id} h={h} categoryMap={catMap} />)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0F172A] border-t border-[#1F2A40] text-charcoal py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-display text-lg">Move Day Pro’s</p>
          <p className="text-sm opacity-70">© 2026 Move Day Pro’s · Move-in day, sorted.</p>
        </div>
      </footer>
    </div>
  );
}
